import EventEmitter from "events"
import GError from "./GError"
import Cron from "./Cron"

type TGetter<T> = {
    success:(cache:T[]) => void,
    fail:(e:any) => void
}

export default abstract class RowsCache<T> extends EventEmitter{
    private _cache:null|T[] = null
    private _ids:Record<string,T> = {}
    private _duration:number
    private _expires:number = 0
    private _getters : TGetter<T>[] = []
    private _name : string

    abstract fetch() : Promise<T[]>
    abstract key(row:T) : string

    constructor(name:string, durationInSeconds:number){
        super()
        this._name = name
        this._duration = durationInSeconds
        if (durationInSeconds) Cron.i.start(name, this.collect.bind(this), 60000)
    }

    get isExpired(){return this._expires && Date.now()>=this._expires }

    row(id:string){ return this._ids[id] }

    expire(){
        this._cache = null
        this._ids = {}
        return this
    }

    get() : Promise<T[]>{
        return new Promise(async (success,fail) => {
            if (this._cache && !this.isExpired) return success(this._cache)
            
            this._getters.push({success,fail})

            if (this._getters.length>1) return
            try {
                const ret = await this.fetch()
                this._set(ret)
                const getters = this._getters.splice(0)
                for(const cb of getters) {
                    cb.success(ret)
                }
            }catch(e){
                const getters = this._getters.splice(0)
                for(const cb of getters) {
                    cb.fail(e)
                }
            }
        })
    }

    add(row:T){
        if (!this._cache) throw new GError.InvalidArg(`${this._name} is not initialized`)

        const id = this.key(row)
        const index = this._cache.findIndex(r => this.key(r)==id)

        if (index<0) {
            this._cache.push(row)
            this.onAdd(this._cache, row)
        }
        else {
            const old = this._cache[index]
            this._cache[index] = row
            this.onUpdate(this._cache, row, old)
        }
        this._ids[id] = row
        return row
    }

    remove(row:T){
        if (!this._cache) throw new GError.InvalidArg(`${this._name} is not initialized`)
            
        const id = this.key(row)
        const index = this._cache.findIndex(r => this.key(r)==id)
        if (index>=0) {
            this._cache.splice(index,1)
            this.onRemove(this._cache, row)
        }
        delete this._ids[id]
        return row
    }
    async one(id:string){
        await this.get()
        const ret = this._ids[id]
        if (ret) return ret
        else throw new GError.NotFound(`file id=${id} not found`)
    }

    fromCache(id:string) : T|null{
        return this._ids[id] || null
    }

    private _set(cache:T[]) {
        this._expires = this._duration ? Date.now() + this._duration*1000 : 0
        this._cache = cache
        this._ids = {}
        for(const row of cache){
            this._ids[this.key(row)] = row
        }
        this.onCache(this._cache)
        return this
    }

    protected onCache(cache:T[]){
        this.emit("cache", cache)
    }
    protected onUpdate(cache:T[], row:T, old:T){
        this.emit("update", cache, row, old)
    }
    protected onAdd(cache:T[], row:T){
        this.emit("add", cache, row)
    }
    protected onRemove(cache:T[], row:T){
        this.emit("remove", cache, row)
    }
    protected onCollect(cache:T[]){
        this.emit("collect", cache)
    }

    private collect(next:Function){
        if (this.isExpired) {
            const cache = this._cache
            this._cache = null
            this._expires = 0
            cache && this.onCollect(cache)
        }
        next()
    }
}